import { Fragment } from 'react';
import type { RateioResponse } from '@triade/contracts';
import { usePode } from '@/api/me';
import { formatarMoeda } from '@/lib/format';
import { PT_BR } from '@/lib/i18n-pt';
import { BotaoExpandir, NUMERO, TabelaDestinos, TD_VALOR, TH_VALOR, TotalPorCentro, useExpansao } from './comum';

/** Aba Salarios: colaborador, centro principal, valores (R$ so se a API mandou) e destinos expansiveis. */
export function AbaSalarios({ rateio }: { rateio: RateioResponse }) {
  const { aberta, alternar } = useExpansao();
  const { pode } = usePode();
  const linhas = rateio.salarios;
  const comValor = linhas.some((p) => p.salario !== undefined);
  const comGratificacoes = linhas.some((p) => p.gratificacoes !== undefined);
  const comBonus = linhas.some((p) => p.bonus !== undefined);
  const comTotal = linhas.some((p) => p.total !== undefined);
  const colunas = 3 + (comValor ? 1 : 0) + (comGratificacoes ? 1 : 0) + (comBonus ? 1 : 0) + (comTotal ? 1 : 0);

  if (linhas.length === 0) {
    // Sem rh.ver a API devolve `salarios` vazio (o total por centro continua no mapa): a mensagem de vazio seria enganosa.
    const mensagem = pode('rh.ver') ? 'Nenhum colaborador com salário nesta competência.' : PT_BR['salarios.sem_rh_ver'];
    return <p role="status" className="px-5 py-8 text-center text-small text-ink-muted">{mensagem}</p>;
  }

  const totalSalarios = rateio.totaisAbas?.salarios;

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[640px] border-collapse text-small" aria-label="Salários rateados">
          <thead>
            <tr className="bg-surface-muted text-left text-caption text-ink-muted">
              <th scope="col" className="w-10 px-2 py-2" aria-label="Expandir" />
              <th scope="col" className={`${TH_VALOR} sticky left-0 z-10 bg-surface-muted`}>Colaborador</th>
              <th scope="col" className={TH_VALOR}>Centro principal</th>
              {comValor && <th scope="col" className={`${TH_VALOR} text-right`}>Salário</th>}
              {comGratificacoes && <th scope="col" className={`${TH_VALOR} text-right`}>Gratificações</th>}
              {comBonus && <th scope="col" className={`${TH_VALOR} text-right`}>Bônus e benefícios</th>}
              <th scope="col" className={TH_VALOR}>Terceirizado</th>
              {comTotal && <th scope="col" className={`${TH_VALOR} sticky right-0 z-10 bg-surface-muted text-right`}>Total a ratear</th>}
            </tr>
          </thead>
          <tbody>
            {linhas.map((p) => {
              const ab = aberta(p.parametroId);
              return (
                <Fragment key={p.parametroId}>
                  <tr data-testid="linha-salario-rateio" className="border-t border-line-soft">
                    <td className="px-2 py-2.5 align-top">
                      <BotaoExpandir aberta={ab} rotulo={p.nome} onClick={() => alternar(p.parametroId)} />
                    </td>
                    <td className={`${TD_VALOR} sticky left-0 z-[1] bg-card`}>{p.nome}</td>
                    <td className={`${TD_VALOR} whitespace-nowrap text-ink-secondary`}>{p.centroPrincipal ?? '—'}</td>
                    {comValor && <td className={`${TD_VALOR} ${NUMERO}`}>{formatarMoeda(p.salario)}</td>}
                    {comGratificacoes && <td className={`${TD_VALOR} ${NUMERO}`}>{formatarMoeda(p.gratificacoes)}</td>}
                    {comBonus && <td className={`${TD_VALOR} ${NUMERO}`}>{formatarMoeda(p.bonus)}</td>}
                    <td className={`${TD_VALOR} whitespace-nowrap`}>{p.terceirizado ? 'Sim' : 'Não'}</td>
                    {comTotal && <td className={`${TD_VALOR} ${NUMERO} font-semibold sticky right-0 z-[1] bg-card`}>{formatarMoeda(p.total)}</td>}
                  </tr>
                  {ab && (
                    <tr className="bg-surface-muted">
                      <td />
                      <td colSpan={colunas} className="px-3 pb-4">
                        <TabelaDestinos destinos={p.destinos} rotulo={p.nome} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          {comTotal && totalSalarios !== undefined && (
            <tfoot>
              <tr className="border-t border-border font-semibold">
                <td colSpan={colunas} className={`${TD_VALOR} bg-surface-muted`}>Total</td>
                <td className={`${TD_VALOR} ${NUMERO} sticky right-0 z-[1] bg-surface-muted`} data-testid="total-salarios">{formatarMoeda(totalSalarios)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <TotalPorCentro linha={rateio.mapaCif.find((l) => l.chave === 'SALARIO')} colunas={rateio.colunasMapa} testid="total-geral-salarios" />
    </div>
  );
}
